import Lenis from "lenis";
import "lenis/dist/lenis.css";
import type { SplatViewerElement } from "../index";

/** Same curve and multipliers as finance.asymetric.studio. */
const LENIS_OPTIONS = {
  duration: 1.2,
  easing: (t: number) => 1 - Math.pow(3, -10 * t),
  touchMultiplier: 2,
  wheelMultiplier: 1,
  smoothWheel: true,
  syncTouch: false,
  autoRaf: true,
  overscroll: false,
} as const;

const instances = new Map<HTMLElement, Lenis>();
let scrollPulse = 0;

function wantsSmooth(): boolean {
  return window.innerWidth > 768 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function syncScrollingClass(): void {
  const moving = [...instances.values()].some((lenis) => Boolean(lenis.isScrolling));
  document.documentElement.classList.toggle("lenis-scrolling", moving);
  if (moving) {
    document.querySelector<SplatViewerElement>("splat-viewer")?.notifyHostScroll();
    if (!scrollPulse) {
      scrollPulse = window.requestAnimationFrame(function pulse() {
        syncScrollingClass();
        if ([...instances.values()].some((lenis) => Boolean(lenis.isScrolling))) {
          scrollPulse = window.requestAnimationFrame(pulse);
        } else {
          scrollPulse = 0;
          document.documentElement.classList.remove("lenis-scrolling");
        }
      });
    }
  }
}

function mount(wrapper: HTMLElement): void {
  if (instances.has(wrapper)) {
    return;
  }
  const content = wrapper.querySelector<HTMLElement>(":scope > .smooth-scroll-content");
  if (!content) {
    return;
  }
  const lenis = new Lenis({
    ...LENIS_OPTIONS,
    wrapper,
    content,
  });
  lenis.on("scroll", syncScrollingClass);
  instances.set(wrapper, lenis);
}

function unmount(wrapper: HTMLElement): void {
  const lenis = instances.get(wrapper);
  if (!lenis) {
    return;
  }
  lenis.destroy();
  instances.delete(wrapper);
}

function sync(): void {
  const targets = [...document.querySelectorAll<HTMLElement>("[data-smooth-scroll]")];
  const on = wantsSmooth();
  document.documentElement.classList.toggle("is-smooth-scroll", on);
  for (const wrapper of targets) {
    if (on) {
      mount(wrapper);
    } else {
      unmount(wrapper);
    }
  }
  for (const wrapper of [...instances.keys()]) {
    if (!targets.includes(wrapper)) {
      unmount(wrapper);
    }
  }
  syncScrollingClass();
}

/** Smooth drawer scroll with the same Lenis feel as the finance app. */
export function bindSmoothScroll(): void {
  sync();
  const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  window.addEventListener("resize", sync);
  motion.addEventListener("change", sync);
}
